import { fold } from "./search.ts";

/** What the box is asking for: everything, only actions (`>`), or only headings (`#`). */
export type PaletteMode = "all" | "actions" | "headings";

export function splitQuery(raw: string): { mode: PaletteMode; q: string } {
  const first = raw[0];
  if (first === ">") return { mode: "actions", q: raw.slice(1).trim() };
  if (first === "#") return { mode: "headings", q: raw.slice(1).trim() };
  return { mode: "all", q: raw.trim() };
}

/** Each character of the text on its own, lower case with accents removed ("" for a bare accent). */
function foldedUnits(text: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < text.length; i += 1) out.push(fold(text[i]!));
  return out;
}

/**
 * Where the letters of `q` sit in `label`: a run when the label contains them together, otherwise
 * the letters in order with anything between. Spaces in the query are ignored. Null when the
 * label does not hold them. Positions count characters of `label`.
 */
export function matchPositions(
  label: string,
  q: string,
): { marks: number[]; score: number } | null {
  const letters = Array.from(fold(q)).filter((c) => c.trim() !== "");
  if (letters.length === 0) return { marks: [], score: 0 };
  const units = foldedUnits(label);

  // A run, with the query's own spaces kept: "on slow" inside "On slow mornings".
  const folded = units.join("");
  const wanted = fold(q).trim();
  if (units.every((u) => u.length === 1) && wanted) {
    const at = folded.indexOf(wanted);
    if (at >= 0) {
      const marks: number[] = [];
      for (let i = at; i < at + wanted.length; i += 1) marks.push(i);
      // Starting a word is a better match than starting mid-word.
      const wordStart = at === 0 || /[\s\-_./]/.test(folded[at - 1]!);
      return { marks, score: at * 0.5 + (wordStart ? 0 : 4) };
    }
  }

  const marks: number[] = [];
  let from = 0;
  for (const letter of letters) {
    let found = -1;
    for (let i = from; i < units.length; i += 1) {
      if (units[i]?.[0] === letter) {
        found = i;
        break;
      }
    }
    if (found < 0) return null;
    marks.push(found);
    from = found + 1;
  }
  return { marks, score: 100 + (marks[marks.length - 1]! - marks[0]!) + marks[0]! * 0.5 };
}

/** The label cut into pieces, the matched letters marked, so it can be drawn without any HTML. */
export function segments(label: string, marks: number[]): { text: string; mark: boolean }[] {
  const set = new Set(marks);
  const out: { text: string; mark: boolean }[] = [];
  for (let i = 0; i < label.length; i += 1) {
    const mark = set.has(i);
    const last = out[out.length - 1];
    if (last && last.mark === mark) last.text += label[i];
    else out.push({ text: label[i]!, mark });
  }
  return out;
}

export interface Ranked<T> {
  item: T;
  marks: number[];
}

/**
 * The items that match, best first (ties keep their order). `alt` is a second text to try, such as
 * a file name; a match there still counts but marks nothing in the label.
 */
export function rank<T extends { label: string; alt?: string }>(
  items: T[],
  q: string,
  limit: number,
): Ranked<T>[] {
  const found: { item: T; marks: number[]; score: number; order: number }[] = [];
  items.forEach((item, order) => {
    if (!q) {
      found.push({ item, marks: [], score: 0, order });
      return;
    }
    const hit = matchPositions(item.label, q);
    if (hit) {
      found.push({ item, marks: hit.marks, score: hit.score, order });
      return;
    }
    const other = item.alt ? matchPositions(item.alt, q) : null;
    if (other) found.push({ item, marks: [], score: 200 + other.score, order });
  });
  found.sort((a, b) => a.score - b.score || a.order - b.order);
  return found.slice(0, limit).map(({ item, marks }) => ({ item, marks }));
}

export interface Group<A, N, H> {
  id: "recent" | "actions" | "notes" | "headings";
  title: string;
  rows: (
    | { kind: "action"; ranked: Ranked<A> }
    | { kind: "note"; ranked: Ranked<N> }
    | { kind: "heading"; ranked: Ranked<H> }
  )[];
}

interface Sources<A, N, H> {
  query: string;
  actions: (A & { label: string; common?: boolean })[];
  notes: (N & { label: string; alt?: string })[];
  /** Notes to offer before anything is typed, newest first. */
  recent: (N & { label: string; alt?: string })[];
  headings: (H & { label: string })[];
}

/** The groups the palette shows for what is typed. */
export function buildPalette<A, N, H>({
  query,
  actions,
  notes,
  recent,
  headings,
}: Sources<A, N, H>): Group<A, N, H>[] {
  const { mode, q } = splitQuery(query);
  const groups: Group<A, N, H>[] = [];
  const add = <K extends "action" | "note" | "heading">(
    id: Group<A, N, H>["id"],
    title: string,
    kind: K,
    ranked: Ranked<never>[],
  ) => {
    if (ranked.length === 0) return;
    groups.push({
      id,
      title,
      rows: ranked.map((r) => ({ kind, ranked: r }) as Group<A, N, H>["rows"][number]),
    });
  };

  if (mode === "all" && !q) {
    add("recent", "Recent notes", "note", rank(recent, "", 3) as Ranked<never>[]);
    add(
      "actions",
      "Actions",
      "action",
      rank(
        actions.filter((a) => a.common),
        "",
        6,
      ) as Ranked<never>[],
    );
  } else if (mode === "all") {
    add("actions", "Actions", "action", rank(actions, q, 5) as Ranked<never>[]);
    add("notes", "Notes", "note", rank(notes, q, 4) as Ranked<never>[]);
    add("headings", "In this note", "heading", rank(headings, q, 3) as Ranked<never>[]);
  } else if (mode === "actions") {
    add("actions", "Actions", "action", rank(actions, q, 30) as Ranked<never>[]);
  } else {
    add("headings", "In this note", "heading", rank(headings, q, 30) as Ranked<never>[]);
  }
  return groups;
}
